import React, {
    FocusEventHandler,
    KeyboardEventHandler,
    useEffect,
    useRef,
} from 'react';
import { Brands } from '../../../models/brands';

export type AppTextInputCallables = {
    setInput: (value: number) => void;
};

type inputMode = 'text' | 'currency';

type AppTextInputProps = {
    id?: string;
    name?: string;
    isEnabled?: boolean;
    mode?: inputMode;
    placeholder: string;
    label: string;
    setValue: Function;
    className?: string;
    inputRef?: any;
    errorText?: string;
    brand?: Brands;
    currencySymbol?: string;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    setCallables?: (callables: AppTextInputCallables) => void;
};

const getInputTypeFromMode = (mode: inputMode): string => {
    switch (mode) {
        case 'text':
            return 'text';
        case 'currency':
            return 'tel';
    }
};

const AppTextInput: React.FC<AppTextInputProps> = ({
    id,
    className,
    name,
    isEnabled = true,
    mode = 'text',
    placeholder,
    label,
    setValue,
    inputRef,
    errorText,
    currencySymbol = '£',
    onBlur,
    onFocus,
    onKeyDown,
    setCallables,
}) => {
    const defaultRef = useRef<HTMLInputElement>(null);
    const ref = inputRef ?? defaultRef;

    const inputType = getInputTypeFromMode(mode);

    useEffect(() => {
        if (setCallables) {
            setCallables({
                setInput,
            });
        }
    }, [setCallables]);

    const handleChange = (e: any) => {
        setValue(e.target.value);
    };

    const onFormSubmit = (e: any) => {
        e.preventDefault();
    };

    const setInput = (value: number) => {
        if (ref.current) {
            ref.current.value = value.toFixed(2);
        }
    };

    const onBlurDefault = (e: any) => {
        if (mode === 'currency' && e.target.value) {
            var numberValue = Number(e.target.value);
            if (Number.isNaN(numberValue)) {
                setInput(0);
            } else {
                setInput(Number(numberValue));
            }
        }

        if (onBlur) {
            onBlur(e);
        }
    };

    return (
        <form onSubmit={onFormSubmit} className={`${className}`}>
            <div className="relative">
                {mode === 'currency' && (
                    <div
                        className={`absolute justify-start left-0 z-20 bottom-[13px] pl-6 text-primary-2`}
                    >
                        {currencySymbol}
                    </div>
                )}
                <input
                    id={id}
                    ref={ref}
                    type={inputType}
                    inputMode={inputType === 'tel' ? 'decimal' : 'text'}
                    onChange={handleChange}
                    onBlur={onBlurDefault}
                    onFocus={onFocus}
                    name={name}
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    disabled={!isEnabled}
                    className={`${
                        mode === 'currency' ? 'pl-11' : 'pl-6'
                    } block h-12 pt-3 pb-3 px-2.5 w-full text-primary-1 text-[16px] md:text-sm placeholder-primary-2 
        bg-white rounded-lg border-1 border-gray-300 appearance-none focus:outline-none 
        focus:ring-0 focus:border-mid-blue peer placeholder:opacity-0 focus:placeholder:opacity-60`}
                />
                <label
                    htmlFor={id}
                    className={`absolute text-sm floating-label text-muted-blue duration-200 transform -translate-y-4 
                        top-2 z-10 origin-[0] text-primary-2 scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 
                        peer-focus:top-2 peer-focus:-translate-y-4 ${
                            mode === 'currency'
                                ? 'peer-focus:start-6 peer-placeholder-shown:start-11 start-6'
                                : 'peer-focus:start-4 start-4 peer-placeholder-shown:start-6'
                        }`}
                >
                    <span className="relative z-20 flex gap-1">
                        {label}
                        <span className="text-error">*</span>
                    </span>
                </label>
                <div className="flex items-center align-middle text-xs absolute left-6 -bottom-2 text-error floating-label floating-label--bottom">
                    <span className="relative z-20">{errorText}</span>
                </div>
            </div>
        </form>
    );
};

export default AppTextInput;
